// ============================================================================
// REPORTS API (Phase 3 item 19)
//
// Typed client for /reports endpoints. The types here mirror the shapes
// returned by apps/api/src/services/reportService.ts — keep them in sync when
// the backend payload changes.
// ============================================================================
import { api } from './api';

export type Severity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface ReportFilterInput {
  from?: string; // ISO
  to?: string;   // ISO
  alertType?: string;
  severity?: Severity;
  geofenceId?: string;
  sensorId?: string;
  classification?: string;
  minConfidence?: number;
  model?: string;
  manufacturer?: string;
  serialNumber?: string;
  searchText?: string;
  // Phase 5C — altitude bounds in feet, geofence-breach toggle.
  minAltitudeFt?: number;
  maxAltitudeFt?: number;
  geofenceBreachOnly?: boolean;
}

export interface SummaryKpis {
  totalAlerts: number;
  alertsPerDay: number;
  avgDurationSec: number;
  daysWithAlerts: number;
  maxAlertsPerDay: number;
}

export interface TopDroneRow {
  model: string;
  manufacturer: string | null;
  count: number;
  totalDurationSec: number;
  // Distinct days the model appeared in the range. Used by RankedListCard
  // to flag repeat offenders with a different bar color.
  repeatCount: number;
}

export interface TopSerialRow {
  serialNumber: string;
  count: number;
  totalDurationSec: number;
  repeatCount: number;
}

export interface TopProtocolRow {
  protocol: string;
  count: number;
  totalDurationSec: number;
}

export interface LatestAlertRow {
  id: string;
  createdAt: string;
  type: string;
  severity: Severity;
  title: string;
  message: string;
  durationSec: number;
  zoneName: string | null;
  flag: string | null;
  serialNumber: string | null;
  droneModel: string | null;
  trackId: string | null;
  geofenceId: string | null;
  acknowledged: boolean;
}

export interface DailyBucket {
  date: string; // YYYY-MM-DD
  count: number;
}

// 7 rows (Sun..Sat) × 24 cols (0..23) of alert counts.
export type HourDowMatrix = number[][];

export interface ReportSummary {
  range: { from: string; to: string };
  kpis: SummaryKpis;
  topDrones: TopDroneRow[];
  topSerials: TopSerialRow[];
  topProtocols: TopProtocolRow[];
  latestAlerts: LatestAlertRow[];
  alertsByDate: DailyBucket[];
  alertsByHourDow: HourDowMatrix;
}

export interface AlertDetail extends LatestAlertRow {
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  metadata: Record<string, unknown>;
  track: {
    trackId: string;
    classification: string;
    confidence: number;
    manufacturer: string | null;
    model: string | null;
    serialNumber: string | null;
    registration: string | null;
    firstSeen: string;
    lastSeen: string;
    startLatitude: number | null;
    startLongitude: number | null;
    endLatitude: number | null;
    endLongitude: number | null;
    operatorLatitude: number | null;
    operatorLongitude: number | null;
  } | null;
  geofence: { id: string; name: string; type: string } | null;
  mission: { id: string; name: string; status: string } | null;
}

export interface AlertsListResponse {
  data: LatestAlertRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export const reportsApi = {
  // Single call hydrates every widget. Server caps the range at 2y.
  getSummary: async (filters: ReportFilterInput): Promise<ReportSummary> => {
    const response = await api.get('/reports/summary', { params: filters });
    return response.data.data as ReportSummary;
  },

  listAlerts: async (
    filters: ReportFilterInput,
    page = 1,
    pageSize = 25,
  ): Promise<AlertsListResponse> => {
    const response = await api.get('/reports/alerts', {
      params: { ...filters, page, pageSize },
    });
    return response.data as AlertsListResponse;
  },

  getAlertDetail: async (alertId: string): Promise<AlertDetail> => {
    const response = await api.get(`/reports/alerts/${alertId}`);
    return response.data.data as AlertDetail;
  },

  // POST so long filter combinations bypass query-string length limits.
  // forceRefresh=true busts the server-side in-memory cache.
  getAiSummary: async (
    filters: ReportFilterInput,
    forceRefresh = false,
  ): Promise<AiSummary> => {
    const response = await api.post('/reports/ai-summary', {
      ...filters,
      forceRefresh,
    });
    return response.data.data as AiSummary;
  },
};

export interface AiSummary {
  markdown: string;
  cached: boolean;
  generatedAt: string;
  model: string;
}
